import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/ar-invoices`;

interface Obligation { id: number; description: string; recognition_method: string; allocated_price: number; amount_recognized: number; status: string; contract_number: string; }
interface ScheduleLine { id: number; period: string; scheduled_amount: number; recognized_amount: number; status: string; recognized_date: string | null; }
interface Schedule { obligation_id: number; description: string; recognition_method: string; allocated_price: number; total_scheduled: number; total_recognized: number; total_remaining: number; pct_recognized: number; lines: ScheduleLine[]; }

export default function RevenueSchedule() {
  const { entity } = useGlobalEntity();
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [obligationId, setObligationId] = useState('');
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingObs, setLoadingObs] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  // Load obligations from revenue contracts
  useEffect(() => {
    (async () => {
      setLoadingObs(true);
      try {
        const res = await authFetch(withEntityParam(`${API_V1}/revenue/summary`, entity));
        if (res.ok) {
          await res.json();
          // Fetch contracts to get obligations
          const cRes = await authFetch(withEntityParam(`${API_V1}/revenue/waterfall`, entity));
          if (cRes.ok) {
            const data = await cRes.json();
            const obs: Obligation[] = [];
            for (const c of (data.contracts || [])) {
              for (const ob of (c.obligations || [])) {
                obs.push({
                  id: ob.obligation_id,
                  description: ob.description || `Obligation #${ob.obligation_id}`,
                  recognition_method: ob.recognition_method,
                  allocated_price: ob.allocated_price,
                  amount_recognized: ob.amount_recognized,
                  status: ob.status || (ob.amount_recognized >= ob.allocated_price ? 'COMPLETE' : 'ACTIVE'),
                  contract_number: c.contract_number,
                });
              }
            }
            setObligations(obs);
            if (obs.length > 0 && !obligationId) {
              setObligationId(String(obs[0].id));
            }
          }
        }
      } catch (e) { console.error(e); }
      setLoadingObs(false);
    })();
  }, [entity]);

  // Auto-load schedule when obligation changes
  useEffect(() => {
    if (obligationId) loadSchedule(obligationId);
  }, [obligationId]);

  const loadSchedule = useCallback(async (id?: string) => {
    const targetId = id || obligationId;
    if (!targetId) return;
    setLoading(true);
    try {
      const res = await authFetch(`${API}/schedules/${targetId}`);
      if (res.ok) { setSchedule(await res.json()); }
      else { setSchedule(null); }
    } catch { setSchedule(null); }
    setLoading(false);
  }, [obligationId]);

  const handleGenerate = async () => {
    if (!obligationId) { showToast('Select an obligation', false); return; }
    try {
      const res = await authFetch(`${API}/schedules/${obligationId}/generate`, { method: 'POST' });
      if (res.ok) { showToast('Schedule generated', true); loadSchedule(); }
      else { const e = await res.json(); showToast(e.detail || 'Generation failed', false); }
    } catch { showToast('Failed', false); }
  };

  const handleRecognize = async (period: string) => {
    if (!obligationId) return;
    try {
      const res = await authFetch(`${API}/schedules/${obligationId}/recognize`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period }) });
      if (res.ok) { showToast(`Recognized ${period}`, true); loadSchedule(); }
      else { const e = await res.json(); showToast(e.detail || 'Recognition failed', false); }
    } catch { showToast('Failed', false); }
  };

  const handleRecognizePIT = async () => {
    if (!obligationId) return;
    try {
      const res = await authFetch(`${API}/schedules/${obligationId}/recognize-pit`, { method: 'POST' });
      if (res.ok) { showToast('Revenue recognized (Point-in-Time)', true); loadSchedule(); }
      else { const e = await res.json(); showToast(e.detail || 'Recognition failed', false); }
    } catch { showToast('Failed', false); }
  };

  const selectedOb = obligations.find(o => String(o.id) === obligationId);
  const inputStyle: React.CSSProperties = { padding: '8px 12px', fontSize: 13, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' };

  const columns: Column[] = [
    { key: 'period', label: 'Period', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'scheduled_amount', label: 'Scheduled', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'recognized_amount', label: 'Recognized', align: 'right', render: (v: number, row: ScheduleLine) => <span style={{ color: row.recognized_amount > 0 ? 'var(--positive)' : 'var(--text-secondary)' }}>{formatAmount(v)}</span> },
    { key: 'status', label: 'Status', render: (v: string) => <StatusBadge status={v} /> },
    { key: 'recognized_date', label: 'Date', render: (v: string | null) => <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{v ? new Date(v).toLocaleDateString() : '-'}</span> },
    { key: 'action', label: 'Action', render: (_: any, row: ScheduleLine) => row.status === 'SCHEDULED' ? <ActionButton label="Recognize" onClick={() => handleRecognize(row.period)} variant="success" size="sm" /> : null },
  ];

  return (
    <PageLayout title="Revenue Schedule" subtitle="View and manage recognition schedules for performance obligations">
      {/* Obligation Selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        <select
          value={obligationId}
          onChange={e => setObligationId(e.target.value)}
          style={{ ...inputStyle, minWidth: 400 }}
          disabled={loadingObs}
        >
          <option value="">{loadingObs ? 'Loading obligations...' : 'Select Performance Obligation'}</option>
          {obligations.map(ob => (
            <option key={ob.id} value={String(ob.id)}>
              #{ob.id} — {ob.description} | {ob.recognition_method} | {formatAmount(ob.allocated_price)} | {ob.contract_number}
            </option>
          ))}
        </select>
        {selectedOb?.recognition_method === 'RATABLE' && (
          <ActionButton label="Generate Schedule" onClick={handleGenerate} variant="secondary" />
        )}
        {selectedOb?.recognition_method === 'POINT_IN_TIME' && selectedOb.status !== 'COMPLETE' && (
          <ActionButton label="Recognize Revenue (PIT)" onClick={handleRecognizePIT} variant="success" />
        )}
      </div>

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : !schedule && !selectedOb ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Select a performance obligation above to view its recognition schedule.</div>
      ) : selectedOb?.recognition_method === 'POINT_IN_TIME' ? (
        <div>
          <KpiGrid columns={4} items={[
            { label: 'Allocated Price', value: formatAmountCompact(selectedOb.allocated_price) },
            { label: 'Recognized', value: formatAmountCompact(selectedOb.amount_recognized), color: 'var(--positive)' },
            { label: 'Remaining', value: formatAmountCompact(selectedOb.allocated_price - selectedOb.amount_recognized), color: 'var(--warning)' },
            { label: 'Status', value: selectedOb.status, color: selectedOb.status === 'COMPLETE' ? 'var(--positive)' : 'var(--accent)' },
          ]} />
          <div style={{ padding: 16, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', marginTop: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Point-in-time obligations are recognized immediately upon delivery/completion — no monthly schedule.
              {selectedOb.status === 'COMPLETE' ? ' Revenue has been fully recognized.' : ' Click "Recognize Revenue (PIT)" to recognize the full amount.'}
            </div>
          </div>
        </div>
      ) : schedule ? (
        <div>
          <KpiGrid columns={5} items={[
            { label: 'Allocated', value: formatAmountCompact(schedule.allocated_price) },
            { label: 'Scheduled', value: formatAmountCompact(schedule.total_scheduled), color: 'var(--accent)' },
            { label: 'Recognized', value: formatAmountCompact(schedule.total_recognized), color: 'var(--positive)' },
            { label: 'Remaining', value: formatAmountCompact(schedule.total_remaining), color: 'var(--warning)' },
            { label: 'Progress', value: `${schedule.pct_recognized}%`, color: 'var(--accent)' },
          ]} />

          <div style={{ marginBottom: 12, padding: '10px 14px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>Obligation #{schedule.obligation_id}</span>
            <span style={{ color: 'var(--text-secondary)', marginLeft: 12 }}>{schedule.description || 'No description'}</span>
            <span style={{ color: 'var(--text-hint)', marginLeft: 12 }}>Method: {schedule.recognition_method}</span>
          </div>

          <DataTable columns={columns} rows={schedule.lines} rowKey="id" emptyMessage='No schedule lines. Click "Generate Schedule" to create.' />
        </div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No schedule found for this obligation.</div>
      )}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
